import { useState, useRef, useEffect } from "react"
import { Plus } from "lucide-react"
import type { LayerPreset } from "@/types/explore"

interface PresetsMenuProps {
  layerPresets: LayerPreset[]
  savePreset: (name: string) => void
  loadPreset: (preset: LayerPreset) => void
  deletePreset: (index: number) => void
}

export const PresetsMenu = ({ layerPresets, savePreset, loadPreset, deletePreset }: PresetsMenuProps) => {
  const [presetsOpen, setPresetsOpen] = useState(false)
  const [presetSaving, setPresetSaving] = useState(false)
  const [presetName, setPresetName] = useState("")
  const presetsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!presetsOpen) return
    const handler = (e: MouseEvent) => {
      if (presetsRef.current && !presetsRef.current.contains(e.target as Node)) setPresetsOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [presetsOpen])

  const handleSavePreset = (name: string) => {
    savePreset(name)
    setPresetSaving(false)
    setPresetName("")
  }

  const handleLoadPreset = (preset: LayerPreset) => {
    loadPreset(preset)
    setPresetsOpen(false)
  }

  return (
    <div data-tour-step="presets-menu" className="relative" ref={presetsRef}>
      <button
        onClick={() => {
          setPresetsOpen(!presetsOpen)
          setPresetSaving(false)
        }}
        className={`cursor-pointer p-1.5 rounded-lg transition-colors duration-150 ${presetsOpen ? "text-primary bg-primary/[0.08]" : "text-gray-400 hover:text-primary hover:bg-gray-100"}`}
        title="Configurations enregistrees"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </button>
      {presetsOpen && (
        <div className="absolute right-0 top-full mt-1 bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl border border-gray-100/50 w-56 z-50 overflow-hidden anim-scale-in">
          {!presetSaving ? (
            <button
              onClick={() => {
                setPresetSaving(true)
                setPresetName("")
              }}
              className="cursor-pointer w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/5 transition-colors border-b border-gray-100 font-medium"
            >
              <Plus className="w-3.5 h-3.5" />
              Enregistrer la config.
            </button>
          ) : (
            <div className="px-3 py-2 border-b border-gray-100">
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && presetName.trim()) handleSavePreset(presetName.trim())
                  if (e.key === "Escape") setPresetSaving(false)
                }}
                placeholder="Nom de la config..."
                autoFocus
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 layer-search focus:outline-none"
              />
              <div className="flex gap-1.5 mt-1.5">
                <button
                  onClick={() => {
                    if (presetName.trim()) handleSavePreset(presetName.trim())
                  }}
                  disabled={!presetName.trim()}
                  className="cursor-pointer flex-1 text-xs font-medium bg-primary text-white rounded-lg px-2 py-1 hover:bg-primary-dark disabled:opacity-40 transition-colors"
                >
                  Enregistrer
                </button>
                <button
                  onClick={() => setPresetSaving(false)}
                  className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 px-2 py-1 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
          {layerPresets.length > 0 ? (
            layerPresets.map((preset, i) => (
              <div
                key={i}
                className="transition-[background-color,padding-left] duration-[100ms,150ms] hover:pl-3 flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              >
                <button
                  onClick={() => handleLoadPreset(preset)}
                  className="cursor-pointer text-sm text-gray-600 flex-1 text-left hover:text-gray-900 transition-colors"
                >
                  {preset.name}
                </button>
                <button
                  onClick={() => deletePreset(i)}
                  className="cursor-pointer text-gray-300 hover:text-error text-sm ml-2 transition-colors"
                >
                  &times;
                </button>
              </div>
            ))
          ) : (
            <div className="px-3 py-3 text-xs text-gray-400 text-center">Aucune configuration</div>
          )}
        </div>
      )}
    </div>
  )
}
